/** SA-53: "Add from catalog" — client-side filtered product picker for the
 *  manual-order modal. Deployment catalogs are small; no server search. */

import { useMemo, useState } from 'react'
import { BookOpen } from 'lucide-react'
import { Button, Input } from '@/components/ui'
import { formatCurrency } from '@/utils/currency'

export interface CatalogProduct {
  id: string
  name: string
  alias: string | null
  category: string
  price_cents: number | null
}

interface CatalogPickerProps {
  products: CatalogProduct[]
  onPick: (p: CatalogProduct) => void
}

export function CatalogPicker({ products, onPick }: CatalogPickerProps) {
  const [openList, setOpenList] = useState(false)
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return products
    return products.filter(p =>
      p.name.toLowerCase().includes(q)
      || (p.alias ?? '').toLowerCase().includes(q)
      || p.category.toLowerCase().includes(q))
  }, [products, query])

  return (
    <div className="space-y-1">
      <Button type="button" variant="ghost" size="sm"
        leftIcon={<BookOpen className="w-4 h-4" />}
        className="text-primary"
        onClick={() => setOpenList(v => !v)}>
        Add from catalog
      </Button>
      {openList && (
        <div className="rounded-lg border border-border p-2 space-y-1">
          <Input placeholder="Search catalog" aria-label="Search catalog"
            value={query} onChange={e => setQuery(e.target.value)} />
          <ul className="max-h-40 overflow-y-auto">
            {filtered.map(p => (
              <li key={p.id}>
                <button type="button"
                  onClick={() => { onPick(p); setOpenList(false); setQuery('') }}
                  className="w-full flex justify-between px-2 py-1.5 text-sm rounded hover:bg-muted text-left">
                  <span>{p.name}</span>
                  <span className="text-muted-foreground">
                    {p.price_cents != null ? formatCurrency(p.price_cents) : 'Quote'}
                  </span>
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-2 py-1.5 text-sm text-muted-foreground">No matches</li>
            )}
          </ul>
        </div>
      )}
    </div>
  )
}
